import React, { type MouseEvent, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { getPackageSavings } from '@/app/components/search/components/Modal/Modals/PackageSavingsModal/getPackageSavings.tsx';
import { hasPackageSavingsEnabled } from '@/app/components/search/utils/productsResponse.ts';
import { getAdultsChildrenCount } from '@/utils/peopleUtils.ts';
import { formatPrice } from '@/app/components/search/utils/formatting.ts';
import usePriceMode from '@/app/components/search/hooks/usePriceMode.ts';
import AddElementToLastWordInString from '@/app/common/components/Utility/AddElementToTheEndOfString';
import { useReopenGuard } from '@/app/common/hooks/useReopenGuard.ts';
import { Icon } from '@/app/common/icons/Icon';
import { SuccessNearPriceBadgeContainer } from '../BaseBadge/styled';
import type { BadgeContext } from '../types';

const PackageSavingsModal = dynamic(
  () => import('@/app/components/search/components/Modal/Modals/PackageSavingsModal'),
  { ssr: false }
);

const PackageSavingsBadge = ({ dealCard }: BadgeContext) => {
  const { localization, searchParams } = usePageWrapperData();
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const badgeRef = useRef<HTMLSpanElement>(null);
  const reopenGuard = useReopenGuard();
  const { showTotalPrice } = usePriceMode();

  if (!hasPackageSavingsEnabled(dealCard, localization)) return null;

  const { totalSavings } = getPackageSavings(dealCard, localization);

  if (!totalSavings || totalSavings <= 0) return null;

  const travellersCount = getAdultsChildrenCount(searchParams) || 1;
  const savingsPerPerson = totalSavings / travellersCount;

  if (savingsPerPerson <= 0) return null;

  const amount = formatPrice({ price: showTotalPrice ? totalSavings : savingsPerPerson, localization });
  const text = t(showTotalPrice ? 'vacation.oss.cards.usp.save_total' : 'vacation.oss.cards.usp.save_pp', { amount });

  const handleInfoClick = (e: MouseEvent<HTMLButtonElement>) => {
    // Radix portals synthetic click events from the overlay through the React tree,
    // which can cause the modal to reopen immediately after the backdrop is tapped.
    // Guard by checking that the real DOM target is inside this button.
    if (badgeRef.current && !badgeRef.current.contains(e.nativeEvent.target as Node)) {
      e.stopPropagation();
      e.preventDefault();
      return;
    }
    if (reopenGuard.blocked()) return;
    e.preventDefault();
    e.stopPropagation();
    setShowModal(true);
  };

  const handleModalClose = () => {
    reopenGuard.markClosed();
    setShowModal(false);
  };

  return (
    <span ref={badgeRef}>
      <SuccessNearPriceBadgeContainer
        onClick={handleInfoClick}
        as="button"
        type="button"
        aria-label={t('vacation.oss.package_savings.modal.title')}
      >
        <AddElementToLastWordInString text={text}>
          {/* Doubled so it beats the badge's `img { margin: 0 }` reset. */}
          <Icon
            category="Semantics"
            name="icon-circle-info"
            size={16}
            color="iconSuccess"
            alt=""
            css="&& { margin-left: 8px; }"
          />
        </AddElementToLastWordInString>
        <PackageSavingsModal isOpen={showModal} onClose={handleModalClose} dealCard={dealCard} />
      </SuccessNearPriceBadgeContainer>
    </span>
  );
};

export default PackageSavingsBadge;
